<h3 class="ml-2"><span class="fa-light fa-fw fa-user-shield"></span> {{ 'ACCESS_ROLES' | translate }}</h3>

@if (loading) {
  <img class="fadeLoader centerLoader logoSmall" src="assets/img/loading_logo.svg"/>
} @else if (loadError) {
  <div class="alert alert-danger">{{ 'ROLES_LOAD_FAILED' | translate }}</div>
} @else {
  <div class="row">
    <!-- Role list -->
    <div [ngClass]="selected ? 'col-xl-5' : 'col-12'">
      <div class="card">
        <div class="card-header-white header-underline">
          <h6 class="white-header-text">{{ 'ROLES_LIST_TITLE' | translate }}</h6>
        </div>
        <div class="card-body-white">
          <p class="text-muted">{{ 'ROLES_INTRO' | translate }}</p>

          @if (!settings.customRolesEnabled) {
            <div class="alert alert-info" data-testid="roles-not-enabled">
              <span class="fa-light fa-fw fa-circle-info"></span> {{ 'ROLES_CUSTOM_NOT_ENABLED' | translate }}
            </div>
          }

          @if (canEdit) {
            <div class="mb-3 d-flex align-items-center">
              <button class="btn btn-outline-primary gray-bg" (click)="addRole()" [disabled]="limitReached"
                      data-testid="add-role">
                <span class="fa-light fa-fw fa-plus"></span> {{ 'ROLES_NEW' | translate }}
              </button>
              <small class="text-muted ml-3" data-testid="roles-limit">
                {{ 'ROLES_LIMIT' | translate : { count: customRoleCount, max: settings.maxCustomRoles } }}
              </small>
            </div>
          }

          <table class="table table-borderless table-hover">
            <thead>
              <tr class="border-bottom">
                <th>{{ 'ROLE' | translate }}</th>
                <th class="d-none d-md-table-cell text-nowrap">{{ 'ROLES_ACCESS_SUMMARY' | translate }}</th>
                <th class="text-right text-nowrap">{{ 'ROLES_STAFF_COUNT' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (role of roles; track role.id ?? role.level) {
                <tr class="cursorPointer" [ngClass]="{ 'selected-role': role === selected }" (click)="select(role)"
                    [attr.data-testid]="'role-row-' + (role.id ?? 'builtin-' + role.level)">
                  <td>
                    <span class="fa-light fa-fw role-icon" [ngClass]="roleIcon(role)"></span>
                    <strong>{{ roleName(role) }}</strong>
                    @if (role.builtIn) {
                      <span class="badge badge-light ml-2">{{ 'ROLES_BUILT_IN' | translate }}</span>
                    }
                    @if (role.description) {
                      <div class="small text-muted role-description">{{ role.description }}</div>
                    }
                  </td>
                  <td class="d-none d-md-table-cell small text-muted text-nowrap">
                    {{ 'ROLES_SUMMARY' | translate : summary(role) }}
                  </td>
                  <td class="text-right">{{ role.builtIn ? '–' : role.assignedStaffCount }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Role editor -->
    @if (selected) {
      <div class="col-xl-7">
        <div class="card stickyCard">
          <div class="d-flex justify-content-between card-header-white header-underline align-items-center">
            <h6 class="align-self-center mb-0">
              <span class="fa-light fa-fw" [ngClass]="isNew ? 'fa-plus' : roleIcon(selected)"></span>
              {{ isNew ? ('ROLES_NEW' | translate) : roleName(selected) }}
            </h6>
            <div class="cursorPointer d-flex w-25 align-self-stretch" (click)="close()" data-testid="close-role">
              <span class="fa-light fa-xmark ml-auto mr-3 cursorPointer align-self-center"></span>
            </div>
          </div>

          <div class="card-body-white">
            @if (saveSuccess) {
              <div class="alert alert-success">{{ 'UPDATE_SUCCESS' | translate }}</div>
            }
            @if (saveError) {
              <div class="alert alert-danger" data-testid="role-save-error">{{ saveError }}</div>
            }

            @if (selected.builtIn) {
              <div class="alert alert-info">
                <span class="fa-light fa-fw fa-lock"></span> {{ 'ROLES_BUILT_IN_INFO' | translate }}
                @if (canEdit && !limitReached) {
                  <div class="mt-2">
                    <button type="button" class="btn btn-outline-primary btn-sm" (click)="addRole(selected)" data-testid="copy-role">
                      <span class="fa-light fa-fw fa-copy"></span> {{ 'ROLES_COPY_AS_NEW' | translate }}
                    </button>
                  </div>
                }
              </div>
            }

            <form [formGroup]="form" (ngSubmit)="formSubmitSubject.next()">
              @if (!selected.builtIn) {
                <div class="row">
                  <div class="col-md-7 form-group">
                    <label><strong>{{ 'NAME' | translate }} <span>*</span></strong></label>
                    <input type="text" class="form-control" formControlName="name" maxlength="100" data-testid="role-name">
                    @if (form.controls.name.touched && form.controls.name.invalid) {
                      <span class="validationNotice">{{ 'ROLES_NAME_REQUIRED' | translate }}</span>
                    }
                  </div>
                  <div class="col-md-5 form-group">
                    <label><strong>{{ 'ROLES_ICON' | translate }}</strong></label>
                    <div class="icon-picker">
                      @for (icon of icons; track icon) {
                        <button type="button" class="icon-choice" [ngClass]="{ active: form.value.icon === icon }"
                                [disabled]="readOnly" (click)="form.patchValue({ icon: icon }); form.markAsDirty()">
                          <span class="fa-light fa-fw" [ngClass]="icon"></span>
                        </button>
                      }
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label><strong>{{ 'DESCRIPTION' | translate }}</strong></label>
                  <input type="text" class="form-control" formControlName="description" maxlength="255" data-testid="role-description">
                </div>

                @if (!readOnly) {
                  <div class="form-group">
                    <mat-form-field class="selector-full-width w-100">
                      <mat-label>{{ 'ROLES_START_FROM' | translate }}</mat-label>
                      <mat-select (selectionChange)="startFrom($event.value)" data-testid="role-start-from">
                        @for (role of otherRoles; track role.id ?? role.level) {
                          <mat-option [value]="role">{{ roleName(role) }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  </div>
                }
              }

              <!-- Permissions per area -->
              <div class="divider"></div>
              <h6 class="mt-3">{{ 'ROLES_PERMISSIONS' | translate }}</h6>
              <p class="small text-muted">{{ 'ROLES_PERMISSIONS_HELP' | translate }}</p>

              <table class="table table-borderless permissions-table horizontal-scroll">
                <tbody>
                  @for (row of visibleRows; track row.area.key) {
                    <tr [ngClass]="{ 'child-row': row.hasParent }" [attr.data-testid]="'area-' + row.area.key">
                      <td class="area-name" [style.padding-left.rem]="0.75 + row.depth * 1.5">
                        @if (row.hasParent) {
                          <span class="fa-light fa-fw fa-turn-down-right text-muted"></span>
                        }
                        {{ row.area.displayNameKey | translate }}
                        @if (choices[row.area.key] === 'Inherit') {
                          <span class="small text-muted ml-1" data-testid="inherit-hint">({{ levelLabel(effective(row.area.key)) }})</span>
                        }
                        @if (needsPageAccess(row); as pageName) {
                          <small class="d-block text-muted" [attr.data-testid]="'needs-page-' + row.area.key">
                            <span class="fa-light fa-fw fa-circle-info"></span>
                            {{ 'ROLES_NEEDS_PARENT_ACCESS' | translate : { name: pageName } }}
                          </small>
                        }
                        @if (isExpandable(row)) {
                          <span class="sub-area-toggle ml-2" role="button" tabindex="0"
                                [ngClass]="{ locked: isExpanded(row) && !expanded.has(row.area.key) }"
                                (click)="toggleExpanded(row)" (keydown.enter)="toggleExpanded(row)"
                                [attr.data-testid]="'expand-' + row.area.key">
                            <span class="fa-light fa-fw" [ngClass]="isExpanded(row) ? 'fa-chevron-up' : 'fa-chevron-down'"></span>
                            {{ 'ROLES_SUB_AREAS' | translate : { count: childrenOf(row.area.key).length } }}
                          </span>
                        }
                      </td>
                      <td class="text-right text-right-width">
                        <mat-button-toggle-group [value]="choices[row.area.key]" [disabled]="readOnly"
                                                 (change)="setChoice(row.area.key, $event.value)" hideSingleSelectionIndicator>
                          @if (row.hasParent) {
                            <mat-button-toggle value="Inherit" [disabled]="readOnly || inheritAboveOwnAccess(row)"
                                               matTooltip="{{ 'ROLES_INHERIT_TOOLTIP' | translate : { level: levelLabel(effective(row.area.parentKey)) } }}">
                              {{ 'ACCESS_INHERIT' | translate }}
                            </mat-button-toggle>
                          }
                          <mat-button-toggle value="None">{{ 'ACCESS_NONE' | translate }}</mat-button-toggle>
                          <mat-button-toggle value="Read" [disabled]="readOnly || aboveOwnAccess(row.area.key, 'Read')"
                                             [matTooltip]="aboveOwnAccess(row.area.key, 'Read') ? ('ROLES_ABOVE_OWN_ACCESS' | translate) : ''">
                            {{ 'ACCESS_READ' | translate }}
                          </mat-button-toggle>
                          <mat-button-toggle value="ReadWrite" [disabled]="readOnly || aboveOwnAccess(row.area.key, 'ReadWrite')"
                                             [matTooltip]="aboveOwnAccess(row.area.key, 'ReadWrite') ? ('ROLES_ABOVE_OWN_ACCESS' | translate) : ''">
                            {{ 'ACCESS_READWRITE' | translate }}
                          </mat-button-toggle>
                        </mat-button-toggle-group>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>

              @if (!readOnly) {
                <div class="form-group mt-3">
                  @if (exceedsOwnAccess) {
                    <small class="text-danger d-block mb-2" data-testid="role-exceeds-own-access">{{ 'ROLES_ABOVE_OWN_ACCESS' | translate }}</small>
                  }
                  <button type="submit" class="btn btn-primary" [disabled]="!canSave" data-testid="save-role">
                    <span class="fa-light fa-fw fa-floppy-disk"></span> {{ 'SAVE' | translate }}
                  </button>
                  <button type="button" class="btn btn-outline-primary ml-2" (click)="close()">
                    <span class="fa-light fa-fw fa-xmark"></span> {{ 'CANCEL' | translate }}
                  </button>
                  @if (!isNew) {
                    <button type="button" class="btn btn-outline-danger float-right" (click)="delete()" data-testid="delete-role">
                      <span class="fa-light fa-fw fa-trash-alt"></span> {{ 'DELETE' | translate }}
                    </button>
                  }
                </div>
              }
            </form>
          </div>
        </div>
      </div>
    }
  </div>
}
